import { Bell, Hourglass, Repeat } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import TaskFieldsForm, { TaskFieldsValue } from "@/components/tasks/details/TaskFieldsForm/TaskFieldsForm";
import {
  DEFAULT_AREA_COLOR,
  ListColorKey,
  resolveTaskColors,
} from "@/lib/listColors";
import { formatDurationShort } from "@/lib/time";

type RepeatEndType = "endTime" | "count";

export type PatternTemplate = {
  id: number;
  title: string;
  details?: string | null;
  defaultStartTime?: string | null;
  defaultDurationMin?: number | null;
  defaultRepeatEnabled: boolean;
  defaultRepeatIntervalMin?: number | null;
  defaultRepeatEndType?: RepeatEndType | null;
  defaultRepeatEndTime?: string | null;
  defaultRepeatCount?: number | null;
  defaultAlertEnabled: boolean;
  barColor?: string | null;
  bgColor?: string | null;
};

export type PatternAddSectionValue = {
  templateId: number;
  selected: boolean;
  expanded: boolean;
  form: TaskFieldsValue;
};

export type PatternSelectionPayload = {
  templateId: number;
  title?: string;
  details: string | null;
  notes: string | null;
  startTime: string;
  durationMin: number | null;
  repeatEnabled: boolean;
  repeatIntervalMin: number | null;
  repeatEndType: RepeatEndType | null;
  repeatEndTime: string | null;
  repeatCount: number | null;
  alertEnabled: boolean;
  barColor: ListColorKey | null;
  bgColor: ListColorKey | null;
};

const MIN_PER_DAY = 24 * 60;
const REPEAT_HARD_CAP = 200;

function timeStringToMinutesOrNull(value: string): number | null {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = parseInt(match[1]!, 10);
  const m = parseInt(match[2]!, 10);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

export function getRepeatOccurrenceCount(form: TaskFieldsValue): number {
  const repeat = form.repeat;
  if (!repeat?.enabled) return 1;

  const start = timeStringToMinutesOrNull(form.startTime);
  if (start === null) return 1;

  const intervalMin = Math.max(1, parseInt(repeat.intervalMin, 10) || 60);
  if (repeat.endType === "count") {
    const requested = Math.min(
      REPEAT_HARD_CAP,
      Math.max(1, parseInt(repeat.count, 10) || 1),
    );
    let count = 0;
    for (let i = 0; i < requested; i++) {
      if (start + i * intervalMin >= MIN_PER_DAY) break;
      count++;
    }
    return count;
  }

  const end = timeStringToMinutesOrNull(repeat.endTime || "23:59");
  if (end === null) return 1;

  let count = 0;
  for (
    let t = start;
    t <= end && t < MIN_PER_DAY && count < REPEAT_HARD_CAP;
    t += intervalMin
  ) {
    count++;
  }
  return count;
}

export function createPatternAddSection(
  pattern: PatternTemplate,
  fallbackStartTime: string,
  options?: { selected?: boolean; expanded?: boolean },
): PatternAddSectionValue {
  const barColor = (pattern.barColor ?? null) as ListColorKey | null;
  const bgColor = (pattern.bgColor ?? null) as ListColorKey | null;
  return {
    templateId: pattern.id,
    selected: options?.selected ?? false,
    expanded: options?.expanded ?? false,
    form: {
      title: pattern.title,
      details: pattern.details ?? "",
      notes: "",
      startTime: pattern.defaultStartTime ?? fallbackStartTime,
      durationMin: pattern.defaultDurationMin?.toString() ?? "",
      alertEnabled: pattern.defaultAlertEnabled,
      priority: null,
      repeat: {
        enabled: pattern.defaultRepeatEnabled,
        intervalMin: pattern.defaultRepeatIntervalMin?.toString() ?? "60",
        endType: pattern.defaultRepeatEndType ?? "endTime",
        endTime: pattern.defaultRepeatEndTime ?? "",
        count: pattern.defaultRepeatCount?.toString() ?? "",
      },
      customColors: barColor !== null || bgColor !== null,
      barColor,
      bgColor,
    },
  };
}

export function patternSectionToSelection(
  section: PatternAddSectionValue,
): PatternSelectionPayload {
  const repeat = section.form.repeat!;
  return {
    templateId: section.templateId,
    title: section.form.title.trim() || undefined,
    details: section.form.details.trim() || null,
    notes: (section.form.notes ?? "").trim() || null,
    startTime: section.form.startTime,
    durationMin:
      section.form.durationMin.trim() === ""
        ? null
        : Math.max(1, parseInt(section.form.durationMin, 10) || 1),
    repeatEnabled: repeat.enabled,
    repeatIntervalMin: repeat.enabled
      ? Math.max(1, parseInt(repeat.intervalMin, 10) || 60)
      : null,
    repeatEndType: repeat.enabled ? repeat.endType : null,
    repeatEndTime:
      repeat.enabled && repeat.endType === "endTime" ? repeat.endTime || null : null,
    repeatCount:
      repeat.enabled && repeat.endType === "count"
        ? Math.max(1, parseInt(repeat.count, 10) || 1)
        : null,
    alertEnabled: section.form.alertEnabled,
    barColor: section.form.customColors ? section.form.barColor : null,
    bgColor: section.form.customColors ? section.form.bgColor : null,
  };
}

export default function PatternAddSection({
  value,
  onChange,
  listName,
  listColor,
  listBgColor,
}: {
  value: PatternAddSectionValue;
  onChange: (value: PatternAddSectionValue) => void;
  listName: string;
  listColor?: string | null;
  listBgColor?: string | null;
}) {
  const repeatCount = getRepeatOccurrenceCount(value.form);
  const resolved = resolveTaskColors({
    barColor: value.form.barColor as string | null | undefined,
    bgColor: value.form.bgColor as string | null | undefined,
    listColor: listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: listBgColor ?? null,
  });
  const colors = {
    base: resolved.bar,
    soft: resolved.soft,
    ink: resolved.ink,
  };

  function patch(patchValue: Partial<PatternAddSectionValue>) {
    onChange({ ...value, ...patchValue });
  }

  return (
    <div
      className={`rounded-xl overflow-hidden border transition-all ${
        value.selected ? "border-foreground/40" : "border-border/60"
      }`}
      style={{ background: colors.soft }}
    >
      <div className="flex items-stretch">
        <span className="w-1.5" style={{ background: colors.base }} aria-hidden />
        <div className="flex-1 flex items-stretch gap-3 px-4 py-3 min-w-0">
          <button
            type="button"
            onClick={() => patch({ expanded: !value.expanded })}
            className="flex-1 min-w-0 text-left"
          >
            <div
              className="font-semibold text-base leading-snug"
              style={{ color: colors.ink }}
            >
              {value.form.title}
            </div>
            {value.form.details && (
              <div
                className="text-sm mt-1 truncate"
                style={{ color: colors.ink, opacity: 0.75 }}
              >
                {value.form.details}
              </div>
            )}
            {value.form.notes && (
              <div
                className="text-[12px] mt-2 pt-2 border-t whitespace-pre-wrap"
                style={{
                  color: colors.ink,
                  opacity: 0.85,
                  borderColor: `color-mix(in oklch, ${colors.ink} 15%, transparent)`,
                }}
              >
                {value.form.notes}
              </div>
            )}
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] tabular-nums font-mono"
              style={{ color: colors.ink, opacity: 0.7 }}
            >
              <span
                className="inline-flex max-w-40 items-center rounded-full px-2 py-0.5 uppercase tracking-[0.14em] truncate"
                style={{
                  background: `color-mix(in oklch, ${colors.base} 18%, transparent)`,
                  color: colors.ink,
                }}
              >
                {listName}
              </span>
              {value.form.alertEnabled && (
                <span className="inline-flex items-center gap-1" title="Alert on">
                  <Bell className="h-3 w-3" />
                </span>
              )}
              {value.form.repeat?.enabled && (
                <span className="inline-flex items-center gap-1" title="Repeats">
                  <Repeat className="h-3 w-3" />
                  {repeatCount}
                </span>
              )}
              {value.form.durationMin && (
                <span className="inline-flex items-center gap-1" title="Duration">
                  <Hourglass className="h-3 w-3" />
                  {formatDurationShort(parseInt(value.form.durationMin, 10) || 0)}
                </span>
              )}
            </div>
          </button>
          <div
            role="button"
            tabIndex={0}
            onClick={() => patch({ selected: !value.selected })}
            onKeyDown={e => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              patch({ selected: !value.selected });
            }}
            className="flex w-16 shrink-0 cursor-pointer flex-col items-center rounded-lg py-1 transition-colors hover:bg-background/20"
            style={{ color: colors.ink }}
            aria-label={value.selected ? "Deselect task" : "Select task"}
          >
            <span className="time rounded-full px-1.5 py-0.5 text-xs tabular-nums font-mono font-semibold opacity-80">
              {value.form.startTime || "-"}
            </span>
            <Checkbox
              checked={value.selected}
              onCheckedChange={checked => patch({ selected: !!checked })}
              onClick={e => e.stopPropagation()}
              className="my-auto"
              style={{
                backgroundColor: value.selected ? colors.ink : undefined,
                borderColor: colors.ink,
              }}
            />
          </div>
        </div>
      </div>

      {value.expanded && (
        <div className="max-h-[56dvh] overflow-y-auto px-4 pb-4 pt-2 border-t border-border/40 bg-background">
          <TaskFieldsForm
            value={value.form}
            onChange={form => patch({ form })}
            labels={{
              previewLabel: listName,
              fallbackBar: listColor ?? null,
              fallbackBg: listBgColor ?? null,
            }}
          />
        </div>
      )}
    </div>
  );
}
